import Box from '@mui/material/Box';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import { t } from 'i18next';
import { MouseEvent } from 'react';

import {
  THEME_MODE_PREFERENCES,
  ThemeModePreference,
} from '~contexts/ThemeMode/themeModeStorage';
import useThemeMode from '~contexts/ThemeMode/useThemeMode';
import { User } from '~hooks/useUsers/models';
import useUsers from '~hooks/useUsers/useUsers';

interface AppearanceFormProps {
  user: User;
}

export default function AppearanceForm({ user }: AppearanceFormProps) {
  const { mode, setMode } = useThemeMode();
  const { updateUserAppearanceTheme } = useUsers();

  const handleChange = async (
    _event: MouseEvent<HTMLElement>,
    nextMode: ThemeModePreference | null,
  ) => {
    // ToggleButtonGroup emits null when the active button is re-clicked; keep
    // the current selection rather than clearing the theme.
    if (!nextMode) {
      return;
    }

    // Apply instantly (also mirrored to localStorage), then persist to the
    // backend so the choice follows the user across devices.
    setMode(nextMode);

    try {
      await updateUserAppearanceTheme(user.id, nextMode);
    } catch {
      // updateUserAppearanceTheme already surfaced the failure via a toast.
    }
  };

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 1 }}>
        {t('settings.user_profile.appearance.title')}
      </Typography>
      <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
        {t('settings.user_profile.appearance.description')}
      </Typography>
      <ToggleButtonGroup
        exclusive
        value={mode}
        onChange={handleChange}
        size="small"
        aria-label={t('settings.user_profile.appearance.title')}
      >
        {THEME_MODE_PREFERENCES.map((option) => (
          <ToggleButton
            key={option}
            value={option}
            data-test-id={`appearance-theme-${option}`}
          >
            {t(`settings.user_profile.appearance.${option}`)}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
    </Box>
  );
}
